import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { concatMap, filter, take, takeUntil, tap } from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { IntercomService } from '@libs/src/intercom/intercom.service';
import { PostHogService } from '@libs/src/posthog/post-hog.service';
import { AuthService } from '@main-client/src/app/auth/auth.service';

import {
  ResetUnseenConversationsCount,
  ResetUnseenNotificationsCount,
} from './header.actions';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { POSTHOG_FEATURE_FLAGS } from '@libs/src/posthog/posthog-feature-flags.constants';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import {
  FEATURES_SEEN,
  getMembership,
  userHasSeenFeature,
} from '@main-client/src/app/account/account.helpers';
import { GetHubs } from '@main-client/src/app/hubs/hubs.actions';
import { NavigationRouteConfig } from '@main-client/src/app/main-navigation/main-navigation.interfaces';

import { IAppState } from '../app.state';
import { isAnyAdmin, isTenThousandCoffeesUser } from '../user/user.helpers';

import { AIAnnouncementModalService } from '../ai-announcement-modal/ai-announcement-modal.service';
import { LocaleManagerModalService } from '../locale-manager/locale-manager-modal.service';

import isEmpty from 'lodash-es/isEmpty';

const resetCountTypes = {
  conversations: ResetUnseenConversationsCount(),
  notifications: ResetUnseenNotificationsCount(),
};

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-header-nav',
  standalone: false,
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() title: string;
  @Input() routes: NavigationRouteConfig[];
  @Input() user: Account;
  @Input() hasCustomDrawerAction: boolean;
  @Output() customDrawerAction = new EventEmitter();
  isLoading: boolean;
  currentGroup = this.store.select('currentGroup');
  header = this.store.select('header');
  tenant: Tenant;
  showDrawer = false;
  currentMembership: AccountGroupMembership;
  readonly defaultBackgroundColor = '#008564';
  isEligibleForAIAnnouncement = signal(false);
  hasSeenAIAnnouncement = signal(false);

  constructor(
    private readonly accountBackend: AccountBackend,
    private readonly aiAnnouncementModalService: AIAnnouncementModalService,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly localeManagerModalService: LocaleManagerModalService,
    private readonly postHogService: PostHogService,
    private readonly store: Store<IAppState>,
    private readonly intercom: IntercomService,
    public readonly authService: AuthService,
  ) {}

  ngOnInit() {
    if (this.authService.$isLoggedIn()) {
      this.store.dispatch(GetHubs({}));
    }
    this.store
      .select('tenant')
      .pipe(
        filter((tenant: Tenant) => !isEmpty(tenant)),
        tap((result) => {
          this.tenant = result;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnChanges() {
    this.currentGroup.pipe(take(1)).subscribe((item) => {
      if (isEmpty(this.user) || isEmpty(this.user.groups)) {
        this.currentMembership = null;
        return;
      }
      this.currentMembership = item
        ? getMembership(this.user.groups, item)
        : this.user.groups[0];
    });
    if (this.user) {
      this.isEligibleForAIAnnouncement.set(
        this.determineEligibilityForAIAnnouncement(this.user),
      );
      this.hasSeenAIAnnouncement.set(
        userHasSeenFeature(this.user, FEATURES_SEEN.AI_ANNOUNCEMENT_v1_REQUEST),
      );
    }
  }

  determineEligibilityForAIAnnouncement(user: Account) {
    return (
      isAnyAdmin(user) &&
      !isTenThousandCoffeesUser(user) &&
      !this.postHogService.isFeatureEnabled(POSTHOG_FEATURE_FLAGS.AI_BETA_V1)
    );
  }

  logOut() {
    this.authService.logout({ message: 'App header logout button clicked' });
  }

  resetUnseenCount(type: string) {
    this.store.dispatch((resetCountTypes as any)[type]);
  }

  showSideNav(drawer: any) {
    if (this.hasCustomDrawerAction) {
      this.customDrawerAction.emit();
      return;
    }

    this.showDrawer = true;
    drawer.open();
  }

  drawerOpenedChange($event: boolean) {
    this.showDrawer = $event;
  }

  helpCenterClicked() {
    this.intercom.showSpace('help');
  }

  showHelpMessenger() {
    this.intercom.toggleLauncher(false);
  }

  openLanguageManager() {
    this.localeManagerModalService.openLanguageManager(this.user);
  }

  openAIAnnouncementModal() {
    this.aiAnnouncementModalService
      .openDialog({ hasAccess: false })
      .afterClosed()
      .pipe(
        filter(({ shouldRedirect }) => shouldRedirect),
        concatMap(() =>
          this.accountBackend
            .postFeatureSeen(FEATURES_SEEN.AI_ANNOUNCEMENT_v1_REQUEST)
            .pipe(
              tap(() =>
                this.store.dispatch(
                  SetUser({
                    features_seen: [
                      ...(this.user.features_seen ?? []),
                      FEATURES_SEEN.AI_ANNOUNCEMENT_v1_REQUEST,
                    ],
                  }),
                ),
              ),
              takeUntil(this.destroy$),
            ),
        ),
      )
      .subscribe();
  }
}
